import { useState } from 'react';
import { formatPhoneNumber, type TtCustomerCard, type TtMatch } from '@avslack/shared';
import { useQueryClient } from '@tanstack/react-query';
import { savePhoneContact, searchTtCustomers } from '../lib/api';
import { phoneContactsKey, phoneIdentityKey, usePhoneIdentity } from '../data/phone';
import { useCustomerSystem } from '../lib/session';
import { Icon } from './Icon';
import { SalesforceAssign, SalesforceIdentity } from './SalesforceCard';

/**
 * KI HIV? - a TT ugyfelkartyaja a hivas mellett.
 *
 * ─── Mit mutat, es mit NEM ──────────────────────────────────────────────────
 *
 * Cegnev, rendelo, telepules, kapcsolattarto, fordulonap, Cloud, jegyzetek.
 * Penzugyi adat (tartozas, hitelkeret, szamlak) SZANDEKOSAN nincs rajta: ez a
 * kartya minden csengetesnel felugrik mindenkinel, akit csorgetunk, es a
 * tartozas nem az az adat, aminek ott kell lennie ahhoz, hogy valaki felvegye
 * a telefont. Lasd docs/16-tt-ugyfelkartya.md.
 *
 * ─── Negy allapota van, es mind mast jelent ────────────────────────────────
 *
 *  - TOLTES      → meg kerdezzuk a TT-t (a csengetes kozben ez fut le)
 *  - KARTYA      → tudjuk, ki hiv
 *  - VALASZTAS   → tobb ugyfel johet szoba, a kolléga dont
 *  - ISMERETLEN  → a TT nem ismeri a szamot; felajanljuk a hozzarendelest
 *
 * A TT-kimaradas NEM "ismeretlen szam": kulon uzenetet kap. Kulonben a kolléga
 * egy TT-leallas alatt rakotne a szamot egy talalomra valasztott ugyfelre.
 *
 * ─── Mikor lehet ugyfelhez kotni? ───────────────────────────────────────────
 *
 * CSENGETES KOZBEN SOHA (`assignable` hamis). Ilyenkor a kolléga maga sem tudja
 * meg, ki van a szam mogott - csak talalgatna, es a talalgatas ITT beegne a
 * cimjegyzekbe minden kesobbi hivasra. Csengeteskor tehat csak annyit mondunk,
 * hogy ismeretlen a szam.
 *
 * A hozzarendeles a FELVETEL UTAN jon elo, a hivas kozbeni savban: akkor mar
 * elhangzott, hogy ki hiv.
 */
export function CustomerCard({
  number,
  compact,
  assignable = false,
}: {
  number: string;
  compact?: boolean;
  /** Lehet-e innen ugyfelhez kotni a szamot. Csengeteskor NEM - lasd fent. */
  assignable?: boolean;
}) {
  const { data, isLoading } = usePhoneIdentity(number);
  const system = useCustomerSystem();

  if (isLoading) {
    return (
      <Shell>
        <p className="text-xs text-ink-muted">Ügyfél keresése…</p>
      </Shell>
    );
  }
  if (!data) return null;

  // Salesforce-os kolléga: a Salesforce-kartya (docs/24).
  if (system === 'salesforce') {
    return (
      <Shell>
        <SalesforceIdentity number={number} data={data} compact={compact} assignable={assignable} />
      </Shell>
    );
  }

  /*
    NEM TT-s kolléga (ProjectFlow, egyik sem): csak az AVSlack sajat
    cimjegyzeke. TT-kartya, -talalat es "nincs a TT-ben" nem latszik - a
    szerver ilyenkor nem is kerdezi a TT-t.
  */
  if (system !== 'tt') {
    const known = [data.name, data.companyName].filter(Boolean).join(' · ');
    return (
      <Shell>
        <p className="text-xs text-ink-muted">
          {known ? (
            <>
              <span className="font-medium text-ink">{known}</span> · {formatPhoneNumber(number)}
            </>
          ) : (
            `Ismeretlen szám: ${formatPhoneNumber(number)}`
          )}
        </p>
      </Shell>
    );
  }

  if (data.ttDown) {
    return (
      <Shell>
        <p className="text-xs text-ink-muted">
          A TT most nem válaszol – az ügyféladatok kimaradnak. A hívás ettől működik.
        </p>
      </Shell>
    );
  }

  if (data.card)
    return (
      <Shell>
        <CardBody
          card={data.card}
          compact={compact}
          personName={data.personName}
          personRole={data.personRole}
        />
      </Shell>
    );

  if (data.matches.length > 0) {
    return (
      <Shell>
        <p className="mb-2 text-xs text-ink-muted">
          {assignable
            ? 'Több ügyfélhez is tartozik ez a szám. Melyik hív?'
            : 'Több ügyfélhez is tartozik ez a szám:'}
        </p>
        <MatchList number={number} matches={data.matches} assignable={assignable} />
      </Shell>
    );
  }

  return (
    <Shell>
      <Unknown number={number} assignable={assignable} />
    </Shell>
  );
}

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-card border border-border bg-surface-raised p-3">{children}</div>
);

/** Egy cimke-ertek sor. Ures erteknel nem jelenik meg - nincs ures "–" sor. */
function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 text-ink-faint">{label}</span>
      <span className="min-w-0 flex-1 break-words text-ink">{value}</span>
    </div>
  );
}

/** `2027-03-15T00:00:00` → `2027. 03. 15.` Ervenytelen datumnal marad az eredeti. */
function shortDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('hu-HU');
}

function CardBody({
  card,
  compact,
  personName,
  personRole,
}: {
  card: TtCustomerCard;
  compact?: boolean;
  /** A szam tulajdonosa a TT szerint - a `telefon_azonositas`-bol. */
  personName?: string | null;
  personRole?: string | null;
}) {
  const [openNotes, setOpenNotes] = useState(false);
  const notes = [card.megjegyzes, card.jegyzetek, card.kommunikacio].filter(Boolean) as string[];
  const inactive = card.allapot && card.allapot !== 'Aktív';

  return (
    <div>
      <div className="mb-2 flex items-start gap-2">
        <Icon name="cimjegyzek" className="mt-0.5 size-4 shrink-0 text-ink-faint" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{card.ceg ?? card.rendelo}</p>
          {card.rendelo && card.rendelo !== card.ceg && (
            <p className="truncate text-xs text-ink-muted">{card.rendelo}</p>
          )}
        </div>
        {/*
          Az INAKTIV statusz a lenyeg, nem az aktiv: azt kell eszrevenni, ha egy
          mar nem elo ugyfel hiv - az aktiv az alapeset, nem hir.
        */}
        {inactive && (
          <span className="shrink-0 rounded-control bg-danger/10 px-1.5 py-0.5 text-[11px] font-medium text-danger">
            {card.allapot}
          </span>
        )}
      </div>

      <div className="space-y-1">
        {/*
          KI a hivo, ha a TT tudja - ez elore kerul, a kapcsolattarto ELE.
          Egy hivas felvetelenel az szamit, hogy kivel fogunk beszelni; a
          rendelo hivatalos kapcsolattartoja lehet valaki egeszen mas.
        */}
        <Row
          label="Hívó"
          value={personName ? (personRole ? `${personName} · ${personRole}` : personName) : null}
        />
        <Row label="Település" value={card.telepules} />
        {!compact && <Row label="Cím" value={card.cim} />}
        <Row label="Kapcsolattartó" value={card.kapcsolattarto} />
        <Row label="Fordulónap" value={shortDate(card.fordulonap)} />
        {card.cloud && (
          <Row
            label="Cloud"
            value={
              [
                shortDate(card.cloudFordulonap),
                card.cloudMaxUser ? `${card.cloudMaxUser} fő` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'igen'
            }
          />
        )}
        {!compact && <Row label="Képviselő" value={card.kepviselo} />}
      </div>

      {notes.length > 0 && (
        <div className="mt-2 border-t border-border-soft pt-2">
          <button
            type="button"
            aria-expanded={openNotes}
            onClick={() => setOpenNotes((open) => !open)}
            className="flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-ink"
          >
            <Icon name={openNotes ? 'nyilLe' : 'nyilJobbra'} className="size-3" />
            Jegyzetek ({notes.length})
          </button>
          {openNotes && (
            <div className="mt-1.5 space-y-1.5">
              {notes.map((note, index) => (
                <p
                  key={index}
                  className="whitespace-pre-wrap break-words text-xs leading-snug text-ink-muted"
                >
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A talalatok kozul a kolléga valaszt - es a valasztas meg is marad.
 *
 * `assignable` nelkul ugyanez CSAK FELSOROLAS: csengeteskor tajekoztat, kik
 * johetnek szoba, de nem lehet dontenni. A dontes a felvetel utan jon.
 */
function MatchList({
  number,
  matches,
  assignable,
  keepName = null,
  onPicked,
}: {
  number: string;
  matches: TtMatch[];
  assignable: boolean;
  /** A mar beirt nev, amit a hozzarendeles nem irhat felul. */
  keepName?: string | null;
  onPicked?: () => void;
}) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const pick = async (match: TtMatch) => {
    setSaving(match.clinicId);
    setError(false);
    try {
      await savePhoneContact(number, keepName ?? '', {
        companyName: match.ceg ?? match.rendelo ?? '',
        ttClinicId: match.clinicId,
      });
      await queryClient.invalidateQueries({ queryKey: phoneContactsKey });
      await queryClient.invalidateQueries({ queryKey: phoneIdentityKey(number) });
      onPicked?.();
    } catch {
      // Latszodjon, hogy NEM mentodott - kulonben a kolléga azt hinne, kesz.
      setError(true);
    } finally {
      setSaving(null);
    }
  };

  const label = (match: TtMatch) => (
    <>
      <span className="block truncate text-xs font-medium text-ink">
        {match.ceg ?? match.rendelo}
      </span>
      <span className="block truncate text-[11px] text-ink-faint">
        {[match.szemelyNev, match.telepules, match.allapot].filter(Boolean).join(' · ')}
      </span>
    </>
  );

  return (
    <>
      <ul className="space-y-1">
        {matches.map((match) => (
          <li key={match.clinicId}>
            {assignable ? (
              <button
                type="button"
                disabled={saving !== null}
                onClick={() => void pick(match)}
                className="w-full rounded-control border border-border px-2 py-1.5 text-left hover:bg-surface disabled:opacity-50"
              >
                {label(match)}
              </button>
            ) : (
              <div className="rounded-control border border-border-soft px-2 py-1.5">
                {label(match)}
              </div>
            )}
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-danger">
          Nem sikerült menteni a hozzárendelést. Próbáld újra.
        </p>
      )}
    </>
  );
}

/**
 * Ismeretlen szam a kartyan.
 *
 * `assignable` nelkul csak kozli, hogy nem ismerjuk a szamot. Ez a csengetes
 * allapota: ott a kolléga sem tudja meg, ki hiv.
 */
function Unknown({ number, assignable }: { number: string; assignable: boolean }) {
  // flex-wrap: a megnyitott kereso a szoveg ala kerul, nem nyomodik ossze mellette.
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="min-w-0 text-xs text-ink-muted">
        Ismeretlen szám – nincs a TT-ben: {formatPhoneNumber(number)}
      </p>
      {assignable && <AssignCustomer number={number} />}
    </div>
  );
}

/**
 * "Ugyfelhez kotom" - a szam osszekotese egy TT-ugyfellel.
 *
 * ─── Miert csak a hivas UTAN? ───────────────────────────────────────────────
 *
 * Mert a hozzarendeles TARTOS: onnantol minden hivasnal - visszamenoleg a
 * korabbiaknal is - ezt az ugyfelet mutatjuk a szamhoz. Csengetes kozben a
 * kolléga meg nem beszelt a hivoval, tehat csak talalgatna; egy elgepelt
 * talalgatas viszont beegne, es a kovetkezo kolléga mar keszkent kapna.
 *
 * Felvetel utan - a hivas kozbeni savban es a lezart hivas soran - viszont
 * pont ez a hianyzo lepes: elhangzott, ki hiv, es egy kattintassal rogzitheto.
 *
 * ─── Miert van erre egyaltalan szukseg? ────────────────────────────────────
 *
 * Mert a TT keresese a munkatarsak SAJAT mobiljait nem talalja meg (lasd
 * docs/16b-tt-tool-kerelem.md 1. pont), es a hivasok jo resze onnan jon.
 */
export function AssignCustomer({
  number,
  name,
  onDone,
}: {
  number: string;
  /**
   * A szamhoz MAR beirt nev - a hozzarendeles nem torolheti ki.
   *
   * Enelkul a cimjegyzekben egy "Kiss Anna" nevu szamhoz ugyfelet kotve a nev
   * csendben elveszne: a mentes ures nevvel menne fel.
   */
  name?: string | null;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<TtMatch[]>([]);
  const [state, setState] = useState<'idle' | 'searching' | 'down' | 'empty'>('idle');
  // TT-ugyfelhez kotni csak TT-s kolléga tud (a szerver is elutasitja).
  const system = useCustomerSystem();

  const search = async () => {
    if (query.trim().length < 3) return;
    setState('searching');
    const result = await searchTtCustomers(query.trim()).catch(() => null);
    if (!result) return setState('down');
    setMatches(result.matches);
    setState(result.ttDown ? 'down' : result.matches.length === 0 ? 'empty' : 'idle');
  };

  // Salesforce-os kolléga a Salesforce-ban keres; mas nem-TT-s kolléga nem kot ugyfelhez.
  if (system === 'salesforce')
    return <SalesforceAssign number={number} name={name} onDone={onDone} />;
  if (system !== 'tt') return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 rounded-control border border-border px-2 py-1 text-xs font-medium text-ink hover:bg-surface"
      >
        Ügyfélhez kötöm
      </button>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <input
          aria-label="Ügyfél keresése"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && void search()}
          placeholder="Cég, rendelő, település…"
          className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => void search()}
          disabled={query.trim().length < 3}
          className="shrink-0 rounded-control bg-accent px-2 py-1 text-xs font-semibold text-accent-ink disabled:opacity-50"
        >
          Keresés
        </button>
      </div>
      <div role="status">
        {state === 'searching' && <p className="mt-1.5 text-xs text-ink-muted">Keresés…</p>}
        {state === 'empty' && <p className="mt-1.5 text-xs text-ink-muted">Nincs találat.</p>}
        {state === 'down' && (
          <p className="mt-1.5 text-xs text-ink-muted">A TT most nem válaszol.</p>
        )}
      </div>
      {matches.length > 0 && (
        <div className="mt-2">
          <MatchList
            number={number}
            matches={matches}
            assignable
            keepName={name ?? null}
            onPicked={onDone}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="mt-2 text-xs text-ink-muted hover:text-ink"
      >
        Mégse
      </button>
    </div>
  );
}
